import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

function ResetPasswordInner({ token }: { token: string | undefined }) {
	const { t } = useTranslation();
	const { auth } = useApi();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		password: "",
		confirmPassword: "",
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		if (!token) {
			setError(t("auth.reset_password.errors.invalid_token"));
		}
	}, [token, t]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (error) setError(""); // Clear error on input change
	};

	const handleResetPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		// Basic validation
		if (!token) {
			setError(t("auth.reset_password.errors.invalid_token"));
			setIsLoading(false);
			return;
		}

		if (formData.password.length < 6) {
			setError(t("auth.signin.errors.password_min"));
			setIsLoading(false);
			return;
		}

		if (formData.password !== formData.confirmPassword) {
			setError(t("auth.reset_password.errors.passwords_dont_match"));
			setIsLoading(false);
			return;
		}

		try {
			const response = await auth.resetPassword({
				newPassword: formData.password,
				token,
			});

			if (response.error) {
				setError(
					response.error.message ||
						t("auth.reset_password.errors.reset_failed"),
				);
			} else {
				setSuccess(true);
			}
		} catch (err) {
			setError(t("auth.signin.errors.unexpected"));
			console.error("Reset password error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	if (success) {
		return (
			<Layout>
				<div className="p-8 border min-h-full rounded-large bg-gradient-main">
					<div className="max-w-md mx-auto">
						{/* Header */}
						<div className="text-center mb-8">
							<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Icon name="CircleCheck" className="w-8 h-8 text-green-600" />
							</div>
							<h1 className="text-3xl font-manrope font-semibold mb-2">
								{t("auth.reset_password.success_header")}
							</h1>
							<p className="text-muted-foreground">
								{t("auth.reset_password.success_subheader")}
							</p>
						</div>

						{/* Success Card */}
						<Card className="p-6">
							<div className="text-center space-y-4">
								<p className="text-sm text-muted-foreground">
									{t("auth.reset_password.success_detail")}
								</p>
							</div>
						</Card>

						{/* Back to Home */}
						<div className="text-center mt-6">
							<Button variant="ghost" asChild>
								<Link to="/">
									<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
									{t("auth.signin.back_to_home")}
								</Link>
							</Button>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-md mx-auto">
					{/* Header */}
					<div className="text-center mb-8">
						<h1 className="text-3xl font-manrope font-semibold mb-2">
							{t("auth.reset_password.header")}
						</h1>
						<p className="text-muted-foreground">
							{t("auth.reset_password.subheader")}
						</p>
					</div>

					{/* Reset Password Form */}
					<Card className="p-6">
						<form onSubmit={handleResetPassword} className="space-y-4">
							{/* Password Field */}
							<div>
								<Label htmlFor="password">
									{t("auth.reset_password.new_password_label")}
								</Label>
								<Input
									id="password"
									name="password"
									type="password"
									placeholder={t(
										"auth.reset_password.new_password_placeholder",
									)}
									value={formData.password}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Confirm Password Field */}
							<div>
								<Label htmlFor="confirmPassword">
									{t("auth.reset_password.confirm_password_label")}
								</Label>
								<Input
									id="confirmPassword"
									name="confirmPassword"
									type="password"
									placeholder={t(
										"auth.reset_password.confirm_password_placeholder",
									)}
									value={formData.confirmPassword}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Error Message */}
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}

							{/* Submit Button */}
							<Button
								variant="primary"
								type="submit"
								className="w-full"
								disabled={isLoading || !token}
							>
								{isLoading ? (
									<>
										<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
										{t("auth.reset_password.resetting")}
									</>
								) : (
									<>
										<Icon name="Lock" className="w-4 h-4 mr-2" />
										{t("auth.reset_password.submit")}
									</>
								)}
							</Button>
						</form>
					</Card>

					{/* Back to Sign In */}
					<div className="text-center mt-6">
						<p className="text-muted-foreground">
							{t("auth.signup.already_account")}{" "}
							<Link
								to="/auth/signin"
								className="text-primary font-medium hover:underline"
							>
								{t("auth.signup.signin_link")}
							</Link>
						</p>
					</div>

					{/* Back to Home */}
					<div className="text-center mt-4">
						<Button variant="ghost" asChild>
							<Link to="/">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("auth.signin.back_to_home")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
}

export function ResetPasswordParamRoute() {
	const { token } = useParams({ from: "/auth/reset-password/$token" });
	return ResetPasswordInner({ token });
}

export function ResetPasswordQueryRoute() {
	// For `/auth/reset-password?token=...`
	const search = useSearch({ from: "/auth/reset-password" }) as unknown as {
		token?: string | null;
	};
	return ResetPasswordInner({
		token: search.token ? String(search.token) : undefined,
	});
}

export default ResetPasswordParamRoute;
